import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { MULTISELECT_IGNORE_AREA } from '../lib/multiselect';

import type { Position } from '@fabric-app/business/types/global';
import useModifierKeysStore from '@fabric-app/business/store/modifierKeys';
import useMobileSelectionStore from '@fabric-app/business/store/mobileSelection';
import { useReferencedFn } from '@fabric-app/hooks/useReferencedFn';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import equal from 'fast-deep-equal';
import { useClientLayoutEffect } from './useClientLayoutEffect';
import { useHasPointerEvents } from '@fabric-app/hooks/useHasPointerEvents';
import { pick } from '@fabric-app/utils/object/manipulation';

const mergeAndGetUnique = (a: string[], b: string[]) => Array.from(new Set([...a, ...b]));

const assumeClick = (start: Position, end: Position) => {
  const width = Math.abs(start.x - end.x);
  const height = Math.abs(start.y - end.y);
  return width < 10 && height < 10;
};

const getScrollableParent = (element: Element): Element | null => {
  let parent = element.parentElement;

  while (parent && parent !== document.body) {
    const style = window.getComputedStyle(parent);
    if (style.overflow !== 'visible') {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
};

const isElementVisibleInScrollableParent = (element: Element): boolean => {
  const parent = getScrollableParent(element);
  if (!parent) return true;

  const parentRect = parent.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();

  // Check if any part of the element is visible in the scrollable parent
  return (
    elementRect.top < parentRect.bottom &&
    elementRect.bottom > parentRect.top &&
    elementRect.left < parentRect.right &&
    elementRect.right > parentRect.left
  );
};

const getIdsInsideArea = (
  start: Position,
  end: Position,
  options: {
    selector?: string;
  },
) => {
  const selector = options?.selector || '[data-selectable-id]';

  // Calculate boundaries once
  const bounds = {
    left: Math.min(start.x, end.x),
    right: Math.max(start.x, end.x),
    top: Math.min(start.y, end.y),
    bottom: Math.max(start.y, end.y),
  };

  const elements = document.querySelectorAll(selector);
  const result: string[] = [];

  for (const element of elements) {
    const selectableId = element.getAttribute('data-selectable-id');
    if (!selectableId) continue;

    // Check visibility and display only if necessary
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') continue;

    if (!isElementVisibleInScrollableParent(element)) continue;

    const rect = element.getBoundingClientRect();

    // Check if rectangle intersects with selection area
    if (
      rect.right < bounds.left ||
      rect.left > bounds.right ||
      rect.bottom < bounds.top ||
      rect.top > bounds.bottom
    ) {
      continue;
    }

    result.push(selectableId);
  }

  return result;
};

const defaultSelectedIds: string[] = [];
const defaultActiveSelectionIds: string[] = [];

const useDragMultiselect = (
  canMultiSelect: boolean,
  onClearSelection?: () => void,
  _selectionFilter: (id: string) => boolean = () => true,
  /**
   * Selects items only within the layer with multiSelectLayerId
   * Mark your element with data-multiselect-layer-id="your-layer-id"
   * BUG FUT-3367, when selecting on search overlay, it was selecting items underneath as well, e.g. when on folders page
   */
  multiSelectLayerId?: string,
  /**
   * Container that has elements that can be selected, used to check if is available to select
   */
  multiSelectContainerRef?: React.RefObject<HTMLElement> | HTMLElement | null,
) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(defaultSelectedIds);
  const [activeSelectionIds, setActiveSelectionIds] = useState<string[]>(defaultActiveSelectionIds);

  const { meta, shift } = useModifierKeysStore((s) => pick(s, ['meta', 'shift']), shallow);
  const modifierOperation = meta || shift;

  const [dragState, setDragState] = useState({
    multiSelect: false,
    isMouseDown: false,
    isDragging: false,
    startPosition: null as Position | null,
    endPosition: null as Position | null,
  });

  const hasPointerEvents = useHasPointerEvents(multiSelectContainerRef ?? null);

  const { mobileSelectModeIsActive, disableMobileSelectMode } = useMobileSelectionStore(
    (state) => ({
      mobileSelectModeIsActive: state.mobileSelectModeIsActive,
      disableMobileSelectMode: state.disableMobileSelectMode,
    }),
    shallow,
  );

  // Prevents unnecessary unmounts of useEffects in case the given _selectionFilter function changes
  const selectionFilter = useReferencedFn(_selectionFilter);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(defaultSelectedIds);
    if (onClearSelection) onClearSelection();
  }, [onClearSelection]);

  useEffect(() => {
    if (!hasPointerEvents) return;

    const keyDownHandler = (e: KeyboardEvent) => {
      // if esc we also want to clear the selection
      if (e.key === 'Escape') {
        setDragState((prev) => ({
          ...prev,
          multiSelect: false,
          startPosition: null,
          endPosition: null,
        }));
        setActiveSelectionIds(defaultActiveSelectionIds);
        handleClearSelection();
        return;
      }
    };

    window.addEventListener('keydown', keyDownHandler);
    return () => {
      window.removeEventListener('keydown', keyDownHandler);
    };
  }, [handleClearSelection, hasPointerEvents]);

  const { isMobileView } = useResponsive();

  useEffect(() => {
    if (isMobileView || !hasPointerEvents) return;

    const handleOnMouseDown = (e: MouseEvent) => {
      // if the targetElement or an ancestor don't contain the data-multiselect-allow attribute
      // we ignore
      if (!e.target) return;
      const targetElement = e.target as HTMLElement;

      if (
        targetElement.closest('[data-multiselect-clear]') ||
        targetElement.hasAttribute('data-multiselect-clear')
      ) {
        handleClearSelection();
        return;
      }

      if (
        !(
          targetElement.hasAttribute('data-multiselect-allow') ||
          targetElement.closest('[data-multiselect-allow]')
        )
      ) {
        return;
      }

      if (e.button !== 0 || !canMultiSelect) return;

      if (modifierOperation) {
        setDragState((prev) => ({
          ...prev,
          endPosition: { x: e.clientX, y: e.clientY },
        }));
        e.stopPropagation();
        e.preventDefault();
      } else {
        setDragState((prev) => ({
          ...prev,
          endPosition: null,
        }));
      }

      setDragState((prev) => ({
        ...prev,
        multiSelect: true,
        isMouseDown: true,
        startPosition: { x: e.clientX, y: e.clientY },
      }));
    };

    window.addEventListener('mousedown', handleOnMouseDown);
    return () => window.removeEventListener('mousedown', handleOnMouseDown);
  }, [canMultiSelect, modifierOperation, handleClearSelection, isMobileView, hasPointerEvents]);

  useEffect(() => {
    if (!mobileSelectModeIsActive) {
      setDragState({
        multiSelect: false,
        isDragging: false,
        isMouseDown: false,
        startPosition: null,
        endPosition: null,
      });
      setActiveSelectionIds(defaultActiveSelectionIds);
      handleClearSelection();
    }
  }, [mobileSelectModeIsActive, handleClearSelection]);

  const recalculateActiveSelection = useCallback(
    (
      startPosition: Position,
      endPosition: Position,
      modifierOperation: boolean,
      multiSelectLayerId?: string,
    ) => {
      const width = Math.abs(startPosition.x - endPosition.x);
      const height = Math.abs(startPosition.y - endPosition.y);
      const area = width * height;

      if (area < MULTISELECT_IGNORE_AREA && !modifierOperation) return;

      const foundIds = getIdsInsideArea(startPosition, endPosition, {
        selector: multiSelectLayerId
          ? `[data-multiselect-layer-id="${multiSelectLayerId}"] [data-selectable-id]`
          : undefined,
      });
      setActiveSelectionIds((prev) => {
        return equal(prev, foundIds) ? prev : foundIds;
      });

      return foundIds;
    },
    [],
  );

  useEffect(() => {
    if (
      !dragState.multiSelect ||
      mobileSelectModeIsActive ||
      !dragState.startPosition ||
      !hasPointerEvents
    )
      return;

    const handleOnMouseUp = (e: MouseEvent) => {
      if (e.button !== 0) return;

      // if shift key or cmd is not pressed we clear the multiSelectedFdocs
      if (!modifierOperation) handleClearSelection();
      else
        setDragState((prev) => ({
          ...prev,
          endPosition: { x: e.clientX, y: e.clientY },
        }));

      const selection =
        recalculateActiveSelection(
          dragState.startPosition!,
          {
            x: e.clientX,
            y: e.clientY,
          },
          modifierOperation,
          multiSelectLayerId,
        ) ?? [];

      /**
       * unselect if modifier operation and not much of a movement and single item
       * assuming click to deselect
       */
      if (
        modifierOperation &&
        selection.length === 1 &&
        assumeClick(dragState.startPosition!, { x: e.clientX, y: e.clientY })
      ) {
        /**
         * else merge items
         */
        setSelectedIds((prev) => {
          if (prev.includes(selection[0])) {
            return prev.filter((id) => id !== selection[0]);
          }
          return [...prev, selection[0]];
        });
      } else {
        setSelectedIds((prev) => {
          const newArray = mergeAndGetUnique(prev, selection).filter(selectionFilter.current);
          return equal(newArray, prev) ? prev : newArray;
        });
      }

      setDragState({
        multiSelect: false,
        isDragging: false,
        isMouseDown: false,
        startPosition: null,
        endPosition: null,
      });
      setActiveSelectionIds(defaultActiveSelectionIds);
    };

    const handleOnMouseMove = (e: MouseEvent) => {
      if (dragState.isMouseDown) {
        setDragState((prev) => ({
          ...prev,
          isDragging: true,
        }));
      }
      setDragState((prev) => {
        const prevEndPosition = prev.endPosition;
        const nextEndPosition = { x: e.clientX, y: e.clientY };
        if (equal(prevEndPosition, nextEndPosition)) return prev;
        return {
          ...prev,
          endPosition: nextEndPosition,
        };
      });
    };

    window.addEventListener('mouseup', handleOnMouseUp);
    const t = setTimeout(() => {
      window.addEventListener('mousemove', handleOnMouseMove);
    }, 10);

    return () => {
      window.removeEventListener('mouseup', handleOnMouseUp);
      window.removeEventListener('mousemove', handleOnMouseMove);
      clearTimeout(t);
    };
  }, [
    handleClearSelection,
    modifierOperation,
    dragState.multiSelect,
    mobileSelectModeIsActive,
    dragState.startPosition,
    recalculateActiveSelection,
    multiSelectLayerId,
    selectionFilter,
    dragState.isMouseDown,
    hasPointerEvents,
  ]);

  useEffect(() => {
    if (
      !dragState.multiSelect ||
      !dragState.startPosition ||
      !dragState.endPosition ||
      mobileSelectModeIsActive
    )
      return;

    recalculateActiveSelection(
      dragState.startPosition,
      dragState.endPosition,
      modifierOperation,
      multiSelectLayerId,
    );
  }, [
    dragState.endPosition,
    mobileSelectModeIsActive,
    modifierOperation,
    dragState.multiSelect,
    multiSelectLayerId,
    recalculateActiveSelection,
    dragState.startPosition,
  ]);

  useEffect(() => {
    if (dragState.multiSelect || activeSelectionIds.length === 0 || mobileSelectModeIsActive)
      return;

    // overlap with current selection
    setSelectedIds((prev) => {
      const newArray = mergeAndGetUnique(prev, activeSelectionIds).filter(selectionFilter.current);
      return equal(newArray, prev) ? prev : newArray;
    });

    setActiveSelectionIds(defaultActiveSelectionIds);
  }, [dragState.multiSelect, activeSelectionIds, selectionFilter, mobileSelectModeIsActive]);

  const selectedIdsDiff = useMemo(() => {
    // if modifier operation is active  we return selectedIds and activeSelectionIds merged
    // otherwise we return activeSelectionIds if multiSelect is active
    // or selectedIds if multiSelect is not active
    if (modifierOperation) {
      return mergeAndGetUnique(selectedIds, activeSelectionIds).filter(selectionFilter.current);
    }

    if (dragState.multiSelect) return activeSelectionIds.filter(selectionFilter.current);
    return selectedIds.filter(selectionFilter.current);
  }, [modifierOperation, dragState.multiSelect, selectedIds, activeSelectionIds, selectionFilter]);

  /**
   * Reset selection when user drops objects and are accepted
   */

  useClientLayoutEffect(() => {
    const onDropAccepted = () => {
      setSelectedIds(defaultSelectedIds);
      setActiveSelectionIds(defaultActiveSelectionIds);
      setDragState((prev) => ({
        ...prev,
        multiSelect: false,
      }));
      disableMobileSelectMode();
    };

    window.addEventListener('object-drag-drop-accepted', onDropAccepted);

    return () => {
      window.removeEventListener('object-drag-drop-accepted', onDropAccepted);
    };
  }, []);

  const returnValue = useMemo(
    () => ({
      selectedIds,
      setSelectedIds,
      activeSelectionIds,
      setActiveSelectionIds,
      canMultiSelect,
      multiSelect: dragState.multiSelect,
      setMultiSelect: (value: boolean) => setDragState((prev) => ({ ...prev, multiSelect: value })),
      modifierOperation,
      startPosition: dragState.startPosition,
      setStartPosition: (position: Position | null) =>
        setDragState((prev) => ({ ...prev, startPosition: position })),
      endPosition: dragState.endPosition,
      setEndPosition: (position: Position | null) =>
        setDragState((prev) => ({ ...prev, endPosition: position })),
      selectedIdsDiff,
      handleClearSelection,
      isDragging: dragState.isDragging,
    }),
    [
      selectedIds,
      activeSelectionIds,
      canMultiSelect,
      dragState.multiSelect,
      dragState.startPosition,
      dragState.endPosition,
      selectedIdsDiff,
      modifierOperation,
      handleClearSelection,
      dragState.isDragging,
    ],
  );

  return returnValue;
};

export default useDragMultiselect;
